import { lazy, Suspense, type ComponentProps, type ComponentType } from 'react';
import {
  Navigate,
  Route,
  type Params,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AuthGate } from './AuthGate';
import { CaseShell } from './CaseShell';
import { ExportsScreen } from '../screens/ExportsScreen';
import { CaseWorkspaceShell } from './CaseWorkspaceShell';
import { CollectionSetLegacyRedirect } from './CollectionSetLegacyRedirect';
import { NotFoundScreen } from './NotFoundScreen';
import { RouteError } from './RouteError';
import { LoginScreen } from '../screens/LoginScreen';
import { Skeleton } from '../components/shared/Skeleton';
import { Header } from '../components/Header';
import { useCase, useOptionalCase } from '../contexts/CaseContext';
import { buildCaseKey } from '../hooks/useCaseKey';
import { chromeOwner } from '../lib/breadcrumbTrail';
import { askAiClassicEnabled } from '../lib/askAiClassic';

// #915 — every screen is its own chunk, so a reviewer who only opens the
// table and drawer never downloads the claims-matrix, chronology, discovery,
// or graph stacks. Shared chrome (AuthGate, CaseShell, CaseWorkspaceShell)
// and the two terminal screens (login, not-found) stay eager: login is the
// first paint and not-found is tiny, so neither should pay a chunk round trip.
//
// The typed `C` keeps each wrapper's real prop shape (e.g.
// `IncomingRequestsScreen embedded`) instead of widening every screen.
// `any` is React.lazy's own constraint; prop types still flow through
// `ComponentProps<C>` at every call site.
// Exported so the #1085 fallback regression can render a never-resolving screen
// through the same wrapper the router uses.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyScreen<C extends ComponentType<any>>(
  loader: () => Promise<{ default: C }>,
  Fallback: ComponentType = RouteLoading,
) {
  const Lazy = lazy(loader);
  return function LazyScreen(props: ComponentProps<C>) {
    return (
      <Suspense fallback={<Fallback />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}

// Mirrors the shared Skeleton language used by the tables (#244 / #277) so a
// route transition reads as "loading the screen", not a blank flash.
export function RouteLoading() {
  return (
    <div
      data-testid="route-loading"
      aria-busy="true"
      className="p-8 space-y-4 max-w-5xl mx-auto"
    >
      <Skeleton variant="card" count={3} />
    </div>
  );
}

// Fallback for screens that render their OWN Header (every drilled-in route —
// Document Discovery, Discovery Responses, Chronology, Claims Matrix, Members,
// Ask AI, Workbench — plus /cases). Those screens sit outside
// CaseWorkspaceShell, so the plain skeleton above left nothing but skeleton
// cards while the chunk loaded and the header vanished for ~150–300 ms (#1085).
// Keep the header and breadcrumbs mounted above the same skeleton.
//
// Deliberately NOT the shared fallback: the workspace tabs suspend UNDER the
// shell's own Header, and drawing a second one there would double the chrome.
export function ScreenLoading() {
  // Optional: /cases has no case in context yet; the slug keeps the crumb
  // meaningful until the screen (or CaseShell) supplies a name.
  const caseData = useOptionalCase();
  return (
    <div className="min-h-screen flex flex-col bg-navy">
      <Header caseName={caseData?.case_name ?? caseData?.case_ref} />
      <main id="main" tabIndex={-1} className="flex-1">
        <RouteLoading />
      </main>
    </div>
  );
}

// The Document Collection landing normally renders UNDER the workspace shell's
// Header; only a scoped `?collection=…` / `?set=…` deep link hands the chrome to
// the screen (breadcrumbTrail.chromeOwner — the same predicate the shell and
// IncomingRequestsScreen use). Draw the header here only in that state,
// otherwise the tab would briefly show two (#1085).
export function CollectionLandingLoading() {
  const caseData = useCase();
  const location = useLocation();
  if (chromeOwner(location.pathname, location.search, buildCaseKey(caseData)) !== 'screen') {
    return <RouteLoading />;
  }
  return <ScreenLoading />;
}

const CasesScreen = lazyScreen(
  () => import('../screens/CasesScreen').then((m) => ({ default: m.CasesScreen })),
  ScreenLoading,
);
const LitDeskScreen = lazyScreen(() =>
  import('../screens/LitDeskScreen').then((m) => ({ default: m.LitDeskScreen })),
);
const PleadingsScreen = lazyScreen(() =>
  import('../screens/PleadingsScreen').then((m) => ({ default: m.PleadingsScreen })),
);
const PartiesScreen = lazyScreen(() =>
  import('../screens/PartiesScreen').then((m) => ({ default: m.PartiesScreen })),
);
const ChronologyScreen = lazyScreen(
  () => import('../screens/ChronologyScreen').then((m) => ({ default: m.ChronologyScreen })),
  ScreenLoading,
);
const ClaimsMatrixScreen = lazyScreen(
  () => import('../screens/ClaimsMatrixScreen').then((m) => ({ default: m.ClaimsMatrixScreen })),
  ScreenLoading,
);
const ClaimsMatrixPickerScreen = lazyScreen(
  () =>
    import('../screens/ClaimsMatrixPickerScreen').then((m) => ({
      default: m.ClaimsMatrixPickerScreen,
    })),
  ScreenLoading,
);
const ClaimsMatrixV2Screen = lazyScreen(
  () =>
    import('../screens/ClaimsMatrixV2Screen').then((m) => ({
      default: m.ClaimsMatrixV2Screen,
    })),
  ScreenLoading,
);
const ClaimsMatrixV2PickerScreen = lazyScreen(
  () =>
    import('../screens/ClaimsMatrixV2PickerScreen').then((m) => ({
      default: m.ClaimsMatrixV2PickerScreen,
    })),
  ScreenLoading,
);
const ReviewScreen = lazyScreen(
  () => import('../screens/ReviewScreen').then((m) => ({ default: m.ReviewScreen })),
  ScreenLoading,
);
const DiscoveryWorkspaceScreen = lazyScreen(
  () =>
    import('../screens/DiscoveryWorkspaceScreen').then((m) => ({
      default: m.DiscoveryWorkspaceScreen,
    })),
  ScreenLoading,
);
const IncomingRequestsScreen = lazyScreen(
  () =>
    import('../screens/IncomingRequestsScreen').then((m) => ({
      default: m.IncomingRequestsScreen,
    })),
  CollectionLandingLoading,
);
const IssuesScreen = lazyScreen(() =>
  import('../screens/IssuesScreen').then((m) => ({ default: m.IssuesScreen })),
);
const MembersScreen = lazyScreen(
  () => import('../screens/MembersScreen').then((m) => ({ default: m.MembersScreen })),
  ScreenLoading,
);
const CaseChatScreen = lazyScreen(
  () => import('../screens/CaseChatScreen').then((m) => ({ default: m.CaseChatScreen })),
  ScreenLoading,
);
const GraphSearchScreen = lazyScreen(
  () => import('../screens/GraphSearchScreen').then((m) => ({ default: m.GraphSearchScreen })),
  ScreenLoading,
);

// Tiny helper for case-scoped redirects. Used for legacy URLs that have
// since moved (e.g. /admin → tab home, /lit-desk → tab home).
// `to` also gets the matched params, so a moved route can carry an id across
// (e.g. /chat/<id> → /workbench/<id>).
function CaseRedirect({ to }: { to: (caseKey: string, params: Readonly<Params<string>>) => string }) {
  const params = useParams();
  const { caseKey } = params;
  if (!caseKey) return <Navigate to="/cases" replace />;
  return <Navigate to={to(caseKey, params)} replace />;
}

// /ai/graph-search is the original Ask AI, off unless ASK_AI_CLASSIC_ENABLED. Off, the
// screen is never loaded: the URL lands on the new Ask AI instead.
// Exported for test (routing/AskAiClassicRoute.test.tsx).
export function AskAiClassicRoute() {
  if (!askAiClassicEnabled()) return <CaseRedirect to={(k) => `/cases/${k}/workbench`} />;
  return <GraphSearchScreen />;
}

// The Workbench shipped to its pilot as "Case Chat" at /cases/:caseKey/chat and
// /chat/<chatId>. Those bookmarks land on the same chat under /workbench. The
// id comes back decoded from useParams, so it is re-encoded on the way out.
// Exported for test (routing/LegacyChatRedirect.test.tsx).
export function LegacyChatRedirect() {
  return (
    <CaseRedirect
      to={(k, p) => `/cases/${k}/workbench${p.chatId ? `/${encodeURIComponent(p.chatId)}` : ''}`}
    />
  );
}

// Claims Matrix landing (#620). Same-route param branch mirroring Document
// Discovery's cards-vs-table split (ReviewScreen `showCardsLanding`): no
// `?complaint` → the operative-complaint tile picker; `?complaint=<source_doc_id>`
// present → that complaint's scoped matrix. ClaimsMatrixScreen assumes a real
// `?complaint`, so this guard is what guarantees it never renders scope-less.
// #1093 adds a third arm: `?pleading=<case_pleadings.id>` is the new Cause of
// Action → Contention → Evidence matrix. Availability comes from the
// authenticated installation capability.
export function stripParam(loc: { pathname: string; search: string }, key: string): string {
  const next = new URLSearchParams(loc.search);
  next.delete(key);
  next.delete('claim');
  return `${loc.pathname}${next.size ? `?${next.toString()}` : ''}`;
}

export function ClaimsMatrixRoute() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const caseData = useCase();
  if (searchParams.get('pleading')) {
    if (caseData.claims_matrix_capability?.available) return <ClaimsMatrixV2Screen />;
    return <Navigate to={stripParam(location, 'pleading')} replace />;
  }
  if (searchParams.get('complaint')) return <ClaimsMatrixScreen />;
  if (searchParams.get('legacy') === '1' && caseData.claims_matrix_capability?.legacy_read_available) return <ClaimsMatrixPickerScreen />;
  return caseData.claims_matrix_capability?.available ? (
    <ClaimsMatrixV2PickerScreen />
  ) : (
    <>
      <p role="status" className="m-4 rounded border border-amber-700 p-3 text-sm">
        {caseData.claims_matrix_capability?.reason ?? 'Claims Matrix installation readiness is unavailable. Reload the case after the API is updated.'}
      </p>
      {caseData.claims_matrix_capability?.legacy_read_available ? (
        <><p className="mx-4 text-sm">Legacy Claims Matrix work remains accessible below.</p><ClaimsMatrixPickerScreen /></>
      ) : <p role="alert" className="m-4 text-sm">Reload the case after the API installation is verified. Contact your case administrator if this persists.</p>}
    </>
  );
}
// Route tree. The case workspace presents four top-level tabs (Lit Desk,
// Document Collection, Case Issues, Pleadings) that share a chrome
// (case H1 + tab bar) via <CaseWorkspaceShell>. Drilled-in screens
// (Document Discovery review, Discovery Responses, the Collection
// set-detail view, Members) render outside the shell with their own
// header so they can use the full viewport.
//
// See docs/url-routing-feature.md §3 for the URL contract.
export const router = createBrowserRouter(
  createRoutesFromElements(
    // Pathless wrapper: owns the route-level error UI for every child. This is
    // where a failing lazy-screen import lands (a data router handles route
    // render errors here, not at the app's React ErrorBoundary).
    <Route errorElement={<RouteError />}>
      <Route path="/login" element={<LoginScreen />} />

      <Route element={<AuthGate />}>
        <Route index element={<Navigate to="/cases" replace />} />
        <Route path="/cases" element={<CasesScreen />} />

        <Route path="/cases/:caseKey" element={<CaseShell />}>
          {/* Tabbed case workspace — shared H1 + tab nav above the outlet. */}
          <Route element={<CaseWorkspaceShell />}>
            <Route index element={<LitDeskScreen />} />
            <Route path="collection" element={<IncomingRequestsScreen embedded />} />
            <Route path="issues" element={<IssuesScreen />} />
            <Route path="pleadings" element={<PleadingsScreen />} />
            <Route path="parties" element={<PartiesScreen />} />
          </Route>

          {/* Drilled-in screens — render their own Header, no tabs. */}
          <Route path="documents/review" element={<ReviewScreen />} />
          {/* Background export jobs (#763). Case-scoped and under AuthGate via
              CaseShell — an artifact download must never be reachable
              unauthenticated. */}
          <Route path="exports" element={<ExportsScreen />} />
          <Route path="collection/sets/:setId" element={<CollectionSetLegacyRedirect />} />
          <Route path="members" element={<MembersScreen />} />
          {/* Chronology — reached from the Lit Desk "Chronology" tile (Decision 8). */}
          <Route path="chronology" element={<ChronologyScreen />} />
          {/* Claims Matrix — reached from the Lit Desk "Claims Matrix" tile (#564).
              #620: no ?complaint → the operative-complaint tile picker; a
              ?complaint=<source_doc_id> → that complaint's scoped matrix. */}
          <Route path="claims-matrix" element={<ClaimsMatrixRoute />} />

          {/* The original Ask AI (graph-RAG chat). Dormant behind ASK_AI_CLASSIC_ENABLED
              (lib/askAiClassic.ts): off → this URL redirects to the new Ask AI at
              /workbench; on → the screen as before. Backed by POST
              /cases/{id}/ai/graph-search (api/routes/graph_rag.py), which refuses with
              403 not_enabled unless the API Lambda's ASK_AI_CLASSIC_ENABLED is also on. */}
          <Route path="ai/graph-search" element={<AskAiClassicRoute />} />

          {/* Workbench (internally "Case Chat") — per-case conversations,
              full viewport like Chronology. /workbench is a blank new chat;
              /workbench/<id> is that chat. The optional param keeps the screen
              mounted when the first send rewrites the URL. Reached from the
              Lit Desk "Workbench" tile (pilot-gated by
              GET /cases/{id}/chat/availability); a direct URL for a user
              without access shows the unavailable panel. */}
          <Route path="workbench/:chatId?" element={<CaseChatScreen />} />

          {/* Discovery Responses — all sub-paths render the same screen,
              which reads its current view from the URL. */}
          <Route path="discovery/responses" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/all" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/files" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/uploads" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/intake" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/intake/:intakeId" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/intake/:intakeId/review" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/batch/:batchId" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/compare" element={<DiscoveryWorkspaceScreen />} />
          <Route path="discovery/responses/history" element={<DiscoveryWorkspaceScreen />} />

          {/* Legacy redirects — keep deep bookmarks working for one release. */}
          {/* The pilot's Case Chat URLs, from before it became Workbench. */}
          <Route path="chat/:chatId?" element={<LegacyChatRedirect />} />
          <Route
            path="documents"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="admin"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="lit-desk"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="lit-desk/document-discovery"
            element={
              <CaseRedirect
                to={(k) => `/cases/${k}/documents/review?direction=received`}
              />
            }
          />
          <Route
            path="lit-desk/discovery-responses"
            element={<CaseRedirect to={(k) => `/cases/${k}/discovery/responses`} />}
          />
          <Route
            path="lit-desk/*"
            element={<CaseRedirect to={(k) => `/cases/${k}`} />}
          />
          <Route
            path="discovery/requests"
            element={<CaseRedirect to={(k) => `/cases/${k}/collection`} />}
          />
          <Route
            path="discovery/requests/sets/:setId"
            element={<CollectionSetLegacyRedirect />}
          />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundScreen />} />
    </Route>,
  ),
);
